import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
    EquipmentCheckInOut,
    GetMaterialAccessLogDetailById,
} from '../../../../reduxToolkit/EquipmentAndMaterial/EquipmentApi';
import { CHECK_IN_MODEL } from '../../Guard/IncomingContractor/Model/Model';
import { ACCESS_TYPE_MATERIAL_MODEL } from '../../Contractors/Model/Models';
import HandleChange from '../../../../Helpers/HandleChange';
import { errorMsg } from '../../../../Apis/functions';

/**
 * @component MaterialCheckOutModalApp
 * @description Logic of the exit of a material that is still inside.
 *
 * The material comes from the entry it was registered with, so the detail of the entry is loaded to
 * know which material of it was opened. The employee that takes it out is searched, because it does
 * not have to be the one that brought it in, but it does have to be one that is inside.
 *
 * What the search returns is one option per entry left without its exit, so the employee walks out with
 * the material closing the entry of the option that was picked. An employee with entries of two days
 * appears twice, told apart by the date, and the exit is registered against the one the guard chose.
 *
 * @param {Object} itemSelected - Row selected in the table, its id is the material access log id.
 * @param {Function} onHide - Closes the modal.
 * @param {Function} [onSuccess] - Executed once the exit is stored, before closing.
 * @returns {Object} State and handlers of the modal.
 */
const MaterialCheckOutModalApp = ({ itemSelected, onHide, onSuccess }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const dataUser = useSelector((state) => state.authenticatioauthennSlice?.user?.data);
    const [detail, setDetail] = useState(null);
    const [selectedList, setSelectedList] = useState([]);
    const [emptyFields, setEmptyFields] = useState(false);
    const [item, setItem] = useState({
        ...CHECK_IN_MODEL.set({
            guardId: dataUser?.id,
            guardFullName: dataUser?.fullName,
            accessType: ACCESS_TYPE_MATERIAL_MODEL.EXIT.name,
        }),
        // The guard picks the date of the exit, so the field opens empty instead of with the moment the
        // modal was opened. It has to be null and not undefined for the picker to mark it as missing.
        accessDate: null,
    });
    const { handleChange } = HandleChange({ model: item, setModel: setItem });

    useEffect(() => {
        if (itemSelected?.id) {
            getDetail();
        }
    }, [itemSelected?.id]);

    const getDetail = () => {
        dispatch(GetMaterialAccessLogDetailById(itemSelected?.id)).then(
            ({
                payload: {
                    data: { data, success, code },
                },
            }) => {
                if (success) {
                    setDetail(data);
                } else {
                    errorMsg(code);
                }
            },
        );
    };

    const material = detail?.materials?.[0];

    /**
     * Registers the exit of the material with the employee that takes it out.
     */
    const handleCheckOut = () => {
        const employee = selectedList?.[0];
        // Picked by the guard: an exit older than the entry it closes leaves the employee listed as inside //
        const accessDate = item?.accessDate ? new Date( item.accessDate).getTime() : null;

        if (!employee?.entryId || !material?.id || !accessDate) {
            setEmptyFields(true);
            return;
        }

        const body = {
            ...item.toRegister([{ id: material?.id }]),
            accessDate,
            userId: employee?.userId,
            // The option carries the entry it was listed for, so the exit closes that one and not whichever
            // entry the date lands after: the employee can have older entries of other days still open //
            entryId: employee?.entryId,
        };

        dispatch(EquipmentCheckInOut(body)).then(
            ({
                payload: {
                    data: { success, code },
                },
            }) => {
                if (success) {
                    toast.success(t('has_been_created_successfully'));
                    onSuccess?.();
                    onHide();
                } else {
                    errorMsg(code);
                }
            },
        );
    };

    return {
        t,
        material,
        detail,
        item,
        handleChange,
        selectedList,
        setSelectedList,
        emptyFields,
        handleCheckOut,
    };
};

export default MaterialCheckOutModalApp;
